import { QueryClient } from '@tanstack/react-query';
import { ProblemError } from '@/model';
import type { HyloServices, Update } from '@/model';
import { CommunityAPI } from '../api/client';
import { CommunityDatabase, removeCommunityStores } from './database';
import { CommunityJournal } from './journal';
import { record } from '../availability';
import { saveDraft, settleDrafts } from '../composer/drafts';
const activeScopes = new WeakMap<HyloServices, Set<CommunityRuntime>>();
const removalTasks = new WeakMap<HyloServices, Map<string, Promise<void>>>();
const cleanupSubscriptions = new WeakMap<HyloServices, () => void>();

/** Also installed before auth restoration by the services-phase runtime; no QueryClient or visited route needed. */
export function installCommunityCleanup(services: HyloServices): () => void {
  const previous = cleanupSubscriptions.get(services); if (previous) return previous;
  const origin = location.origin;
  const stop = services.session.onRetire(async event => {
    const retire = async () => {
      const scopes = [...(activeScopes.get(services) ?? [])].filter(scope => scope.accountId === event.accountId);
      await Promise.all(scopes.map(scope => scope.retire()));
      for (const scope of scopes) activeScopes.get(services)?.delete(scope);
    };
    if (event.reason !== 'remove' && event.reason !== 'deleted') { await retire(); return; }
    let tasks = removalTasks.get(services); if (!tasks) { tasks = new Map(); removalTasks.set(services, tasks); }
    let task = tasks.get(event.accountId);
    if (!task) { task = (async () => { await retire(); await removeCommunityStores(origin, event.accountId); })(); tasks.set(event.accountId, task); }
    try { await task; } finally { if (tasks.get(event.accountId) === task) tasks.delete(event.accountId); }
  });
  const dispose = () => { if (cleanupSubscriptions.get(services) === dispose) { cleanupSubscriptions.delete(services); stop(); } };
  cleanupSubscriptions.set(services, dispose); return dispose;
}

function strip(value: unknown, id: string): unknown {
  if (Array.isArray(value)) return value.map(v => strip(v, id));
  if (!record(value)) return value;
  if (value.post_id === id && 'frozen_revision' in value) return undefined;
  if (value.id === id && ('status' in value || 'revision_id' in value)) return { id, status: 'unavailable' };
  return Object.fromEntries(Object.entries(value).map(([key, v]) => [key, strip(v, id)]));
}
export class CommunityRuntime {
  readonly api: CommunityAPI;
  readonly db: CommunityDatabase;
  readonly journal: CommunityJournal;
  readonly prefix: readonly unknown[];
  private epoch = 0;
  private reviewEpoch = 0;
  get publicationEpoch() { return this.reviewEpoch; }
  private activityEpoch = 0;
  get activityVersion() { return this.activityEpoch; }
  setActivityCount(count: number, observedVersion?: number) {
    if (!Number.isSafeInteger(count) || count < 0 || (observedVersion !== undefined && observedVersion !== this.activityEpoch)) return;
    this.activityEpoch++;
    void this.query.cancelQueries({ queryKey: this.key('activityCount'), exact: true });
    this.query.setQueryData(this.key('activityCount'), count);
  }
  private authorization = new AbortController();
  get signal() { return this.authorization.signal; }
  retired = false;
  private listeners = new Set<() => void>();
  private references = 0;
  private dispose?: () => void;
  private draining = false;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private retirement?: Promise<void>;
  readonly loginId: string;
  readonly loginGeneration: number;
  readonly pending = new Set<string>();
  private unavailablePosts = new Set<string>();
  isPostUnavailable(id: string) { return this.unavailablePosts.has(id); }
  constructor(readonly services: HyloServices, readonly query: QueryClient) {
    const session = services.session.state.get();
    this.accountId = session.phase === 'signedIn' ? session.accountId : '';
    const lifetime = services.session.lifetime.get(); this.loginId = lifetime?.loginId ?? ''; this.loginGeneration = lifetime?.generation ?? -1;
    const raw = services.flags.config().get()?.community_context;
    this.realmId = record(raw) && typeof raw.realm_id === 'string' ? raw.realm_id : null;
    this.prefix = ['hylo', location.origin, this.realmId, this.accountId, 'discover'];
    this.db = new CommunityDatabase(JSON.stringify([location.origin, this.realmId, this.accountId]));
    this.api = new CommunityAPI(services, path => this.guard(path), path => path === '/context' ? new AbortController().signal : this.signal); this.journal = new CommunityJournal(this.db, this.api, () => this.scheduleDrain());
    let scopes = activeScopes.get(services); if (!scopes) { scopes = new Set(); activeScopes.set(services, scopes); } scopes.add(this);
    installCommunityCleanup(services);
  }
  readonly accountId: string;
  readonly realmId: string | null;
  guard(path?: string) {
    const state = this.services.session.state.get();
    const lifetime = this.services.session.lifetime.get();
    if (this.retired || state.phase !== 'signedIn' || state.accountId !== this.accountId || !lifetime || lifetime.accountId !== this.accountId || lifetime.loginId !== this.loginId || lifetime.generation !== this.loginGeneration) throw new Error('retired_session');
    const context = this.services.flags.config().get()?.community_context;
    const cached = this.query.getQueryData(this.key('context'));
    if ((path !== '/context' && ((record(context) && context.enabled === false) || (record(cached) && cached.enabled === false))) || (record(context) && record(context.viewer) && typeof context.viewer.account_id === 'string' && context.viewer.account_id !== this.accountId)) throw new Error('community_unavailable');
    if (this.services.flags.flags().get()['community.ui'] === false) throw new Error('community_unavailable');
  }
  key(...suffix: unknown[]) { return [...this.prefix, ...suffix]; }
  snapshot = () => this.epoch;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  bump() { this.epoch++; this.listeners.forEach(fn => fn()); }
  async read<T>(path: string, query?: Record<string, string | number | boolean | null | undefined>, signal?: AbortSignal): Promise<T> {
    const epoch = this.epoch, reviewEpoch = this.reviewEpoch;
    let result: T;
    try { result = await this.api.get<T>(path, query, signal); } catch (error) {
      const id = /^\/posts\/([0-9a-f-]{36})$/.exec(path)?.[1];
      const requestId = /^\/publish-requests\/([0-9a-f-]{36})$/.exec(path)?.[1];
      if (requestId && error instanceof ProblemError && [403, 404, 410].includes(error.status)) { this.bump(); this.query.setQueriesData({ queryKey: this.key('publishRequest', requestId) }, { unavailable: true }); }
      if (error instanceof ProblemError && error.code === 'community_unavailable') this.restrict();
      else if (id && error instanceof ProblemError && (error.status === 404 || error.code === 'blocked')) this.removePost(id);
      throw error;
    }
    if (epoch !== this.epoch || (path.startsWith('/publish-requests/') && reviewEpoch !== this.reviewEpoch)) throw new DOMException('Superseded authorization', 'AbortError');
    if (record(result) && record(result.post) && typeof result.post.id === 'string' && ('revision_id' in result.post || 'draft_revision' in result.post)) this.unavailablePosts.delete(result.post.id);
    if (record(result) && record(result.request) && typeof result.request.post_id === 'string' && this.unavailablePosts.has(result.request.post_id)) throw new DOMException('Revoked publication content', 'AbortError');
    return result;
  }
  invalidate() { void this.query.invalidateQueries({ queryKey: this.prefix }); }
  restrict() {
    if (this.signal.aborted) return;
    const context = this.query.getQueryData(this.key('context')) ?? this.services.flags.config().get()?.community_context;
    this.query.setQueryData(this.key('context'), { viewer: { account_id: this.accountId, can_post: false, can_moderate: false, acting_for: [] }, features: {}, limits: {}, ...(record(context) ? context : {}), enabled: false });
    this.authorization.abort(); this.bump();
    const publicQueries = { queryKey: this.prefix, predicate: (query: { queryKey: readonly unknown[] }) => query.queryKey[this.prefix.length] !== 'context' };
    void this.query.cancelQueries(publicQueries); this.query.removeQueries(publicQueries);
  }
  allow() {
    const raw = this.services.flags.config().get()?.community_context;
    if (!this.retired && this.signal.aborted && this.services.flags.flags().get()['community.ui'] !== false && !(record(raw) && raw.enabled === false)) { this.authorization = new AbortController(); this.bump(); this.invalidate(); this.scheduleDrain(); }
  }
  private scheduleDrain() {
    if (this.retired || this.retryTimer || !this.references || document.hidden) return;
    this.retryTimer = setTimeout(() => { this.retryTimer = undefined; void this.drain(); }, 1500);
  }
  /** Consent-bearing publication operations always wait for an explicit person retry. */
  async drain() {
    if (this.draining || this.retired || document.hidden || this.signal.aborted) return;
    const context = this.services.flags.config().get()?.community_context;
    if (!record(context) || context.enabled !== true) return;
    this.draining = true;
    try {
      this.guard();
      const pending = (await this.db.operations.where('state').anyOf('pending', 'inflight').sortBy('createdAt')).filter(op => Date.now() - op.createdAt < 29 * 86400_000);
      for (const op of pending) {
        if (/\/publish(?:$|\?)|\/publish-requests\//.test(op.frozen.path)) continue;
        if (op.state === 'inflight' && (op.leaseUntil ?? 0) > Date.now()) continue;
        const delay = op.retryAt ?? (op.lastAttemptAt ? op.lastAttemptAt + Math.min(60_000, 1000 * 2 ** Math.min(op.attempt, 6)) : 0);
        if (delay > Date.now()) continue;
        this.guard();
        try { if (op.draftId) await saveDraft(this, op.draftId); else await this.journal.retry(op.id); this.invalidate(); } catch { /* The journal owns recoverable failures. */ }
      }
      if (pending.some(op => !/\/publish(?:$|\?)|\/publish-requests\//.test(op.frozen.path))) this.scheduleDrain();
    } catch { /* Offline, disabled, retired or unavailable storage; keep the originals. */ }
    finally { this.draining = false; }
  }
  removePost(id: string) {
    if (this.unavailablePosts.has(id)) return;
    this.unavailablePosts.add(id);
    this.bump();
    void this.query.cancelQueries({ queryKey: this.prefix });
    this.query.setQueriesData({ queryKey: this.prefix }, value => strip(value, id));
    this.query.removeQueries({ queryKey: this.key('post', id) });
    this.query.removeQueries({ queryKey: this.key('comments', id) });
    this.query.removeQueries({ queryKey: this.key('replies', id) });
  }
  private reauthorizePublication() {
    // A fresh key masks old body AND membership data before any asynchronous reauthorization.
    this.reviewEpoch++; this.bump();
    for (const resource of ['publishRequest', 'requestConversation']) {
      const filter = { queryKey: this.key(resource) };
      void this.query.cancelQueries(filter); this.query.removeQueries(filter);
    }
  }
  private receive = (update: Update) => {
    const data = update.data;
    if (update.type === 'community.post.changed' && typeof data.post_id === 'string') {
      if (data.removed === true) this.removePost(data.post_id);
      else { this.bump(); this.invalidate(); }
    } else if (update.type === 'block.changed') {
      this.bump(); void this.query.cancelQueries({ queryKey: this.prefix }); this.query.removeQueries({ queryKey: this.prefix });
    } else if (update.type === 'community.activity.changed') {
      if (typeof data.unread_count === 'number') this.setActivityCount(data.unread_count);
      if (data.activity_id) void this.query.invalidateQueries({ queryKey: this.key('activity') });
    } else if (update.type === 'community.viewer.changed') {
      if (data.target_kind === 'account' || !['post', 'comment', 'folder'].includes(String(data.target_kind))) this.bump();
      this.invalidate();
    } else if (update.type === 'message.edited') {
      this.reauthorizePublication();
    } else if (['message.deleted', 'message.hidden', 'member.left', 'conversation.readers.changed'].includes(update.type)) {
      this.reauthorizePublication();
    } else this.invalidate();
  };
  retain() {
    this.references++;
    if (this.references === 1) {
      const updates = this.services.sync.onUpdate(['community.post.changed', 'community.viewer.changed', 'community.activity.changed', 'block.changed', 'account.updated', 'me.updated', 'message.edited', 'message.deleted', 'message.hidden', 'member.left', 'conversation.readers.changed'], this.receive);
      const session = this.services.session.state.subscribe(() => { const s = this.services.session.state.get(); if (s.phase !== 'signedIn' || s.accountId !== this.accountId) this.retire(); });
      const lifetime = this.services.session.lifetime.subscribe(() => { const next = this.services.session.lifetime.get(); if (!next || next.loginId !== this.loginId || next.generation !== this.loginGeneration) void this.retire(); });
      let lastContext = this.services.flags.config().get()?.community_context;
      const availability = () => {
        const raw = this.services.flags.config().get()?.community_context;
        if (this.services.flags.flags().get()['community.ui'] === false || (record(raw) && raw.enabled === false)) this.restrict();
        else if (raw !== lastContext && record(raw) && raw.enabled === true) { this.query.setQueryData(this.key('context'), raw); this.allow(); }
        lastContext = raw;
      };
      const flags = this.services.flags.flags().subscribe(availability), config = this.services.flags.config().subscribe(availability);
      const focus = () => { this.bump(); this.invalidate(); this.scheduleDrain(); };
      window.addEventListener('focus', focus);
      const online = this.services.sync.connection.subscribe(() => { if (this.services.sync.connection.get().phase === 'online') focus(); });
      const visible = () => { if (!document.hidden) focus(); };
      document.addEventListener('visibilitychange', visible); this.scheduleDrain();
      this.dispose = () => { updates(); session(); lifetime(); flags(); config(); online(); window.removeEventListener('focus', focus); document.removeEventListener('visibilitychange', visible); if (this.retryTimer) clearTimeout(this.retryTimer); this.retryTimer = undefined; };
    }
    return () => { if (--this.references === 0) { this.dispose?.(); this.dispose = undefined; } };
  }
  retire(): Promise<void> {
    if (this.retirement) return this.retirement;
    this.authorization.abort(); this.retired = true; this.bump(); this.dispose?.(); void this.query.cancelQueries({ queryKey: this.prefix }); this.query.removeQueries({ queryKey: this.prefix });
    this.retirement = (async () => { await Promise.allSettled([this.journal.settled(), settleDrafts(this)]); await this.db.operations.filter(op => ['pending', 'inflight'].includes(op.state)).modify({ state: 'paused' }).catch(() => undefined); this.db.close(); })();
    return this.retirement;
  }
  async mutate<T>(key: string, method: 'POST' | 'PATCH' | 'PUT' | 'DELETE', path: string, body?: Record<string, unknown>) {
    this.guard(); this.pending.add(key);
    try { const result = await this.journal.write<T>(key, method, path, body); this.invalidate(); return result; }
    finally { this.pending.delete(key); }
  }
}
const runtimes = new WeakMap<HyloServices, { account: string; realm: string | null; query: QueryClient; value: CommunityRuntime }>();
export function runtimeFor(services: HyloServices, query: QueryClient) {
  const session = services.session.state.get(), account = session.phase === 'signedIn' ? session.accountId : '';
  const raw = services.flags.config().get()?.community_context, realm = record(raw) && typeof raw.realm_id === 'string' ? raw.realm_id : null;
  const old = runtimes.get(services);
  const lifetime = services.session.lifetime.get();
  if (old && !old.value.retired && old.account === account && old.realm === realm && old.query === query && old.value.loginId === lifetime?.loginId && old.value.loginGeneration === lifetime?.generation) return old.value;
  old?.value.retire(); const value = new CommunityRuntime(services, query); runtimes.set(services, { account, realm, query, value }); return value;
}
